import * as v from 'valibot';
import {
  ScenarioFormFields,
  SelectedBlastModelTypes,
} from 'app/views/blasting/pages/create-scenario/CreateScenario.types';
import { httpClient } from 'core/net/http';
import Guid from 'core/types/Guid';
import { BlastModelType } from 'app/business-logic/domain-models/Blasting';
import { LogManager } from 'core/logging/LogManager';
import { CreateScenarioResponseSchema } from 'app/business-logic/domain-models/Blasting.schema';

const logger = LogManager.getLogger('createNewScenario', { error: { logToServer: true } });

export async function createNewScenario(fields: ScenarioFormFields) {
  const body = mapRequestBody(fields);
  const response = await httpClient.useInternalApi.asAuthorised().post('configuration/blasting/scenarios', body);

  if (!response.isSuccess) {
    const errorMessage = 'Failed to create scenario';
    logger.error(errorMessage);
    throw new Error(errorMessage);
  }

  const parsedData = v.safeParse(CreateScenarioResponseSchema, response.data);

  if (!parsedData.success) {
    const errorMessage = 'Failed to parse "create scenario" response';
    logger.error(errorMessage, parsedData.issues);
    throw new Error(errorMessage);
  }

  return parsedData.output;
}

function mapRequestBody(body: ScenarioFormFields): ICreateScenarioRequest {
  return {
    name: body.name,
    alias: body.alias,
    latitude: body.blastingLocation.latitude as number,
    longitude: body.blastingLocation.longitude as number,
    elevation: body.elevation as number,
    modelTypes: getModelTypes(body.modelTypes),
    utcBlastTimes: body.blastTimes.flatMap(time => {
      const iso = time.toISO();
      if (!iso) return [];
      return [iso];
    }),

    length: body.length as number,
    height: body.height as number,
    width: body.width as number,
    windScientificModelId: body.windScientificModelId,
    modelPollutantId: body.modelPollutantId,
    pollutantScientificModelId: body.pollutantScientificModelId,
    initialConcentrationGramsPerCubicMetre: body.initialConcentrationGramsPerCubicMetre as number,
    displayUnitId: body.displayUnitId,
    forwardTrajectoryScientificModelId: body.forwardTrajectoryScientificModelId,

    azimuth: body.azimuth as number,
    chargeMass: body.chargeMass as number,
    burden: body.burden as number,
    stemmingHeight: body.stemmingHeight as number,
    dispersalAngle: body.dispersalAngle as number,
    holeAngle: body.holeAngle as number,
    flyrockSiteConstantKv: body.flyrockSiteConstantKv as number,

    blastSetup: body.blastSetup,
    holeDiameter: body.holeDiameter as number,
    frontFaceKFactor: body.frontFaceKFactor as number,
    backFaceKFactor: body.backFaceKFactor as number,
    chargeMassPerHole: body.chargeMassPerHole as number,
    groundVibrationSiteConstantKv: body.groundVibrationSiteConstantKv as number,
    groundVibrationSiteExponentKb: body.groundVibrationSiteExponentKb as number,
  };
}

function getModelTypes(modelTypes: SelectedBlastModelTypes): string[] {
  return Object.keys(modelTypes).filter(modelType => modelTypes[modelType as BlastModelType]);
}

interface ICreateScenarioRequest {
  name: string;
  alias: string;
  latitude: number;
  longitude: number;
  elevation: number;
  modelTypes: string[];
  utcBlastTimes: string[];

  length: number;
  height: number;
  width: number;
  windScientificModelId: Guid;
  modelPollutantId: Guid | null;
  pollutantScientificModelId: Guid | null;
  initialConcentrationGramsPerCubicMetre: number;
  displayUnitId: Guid | null;
  forwardTrajectoryScientificModelId: Guid;

  azimuth: number;
  chargeMass: number;
  burden: number;
  stemmingHeight: number;
  dispersalAngle: number;
  holeAngle: number;
  flyrockSiteConstantKv: number;

  blastSetup: string;
  holeDiameter: number;
  frontFaceKFactor: number;
  backFaceKFactor: number;
  chargeMassPerHole: number;
  groundVibrationSiteConstantKv: number;
  groundVibrationSiteExponentKb: number;
}
